import React, { useMemo, useRef, useState } from "react";
import styles from "./styles.module.scss";
import Button from "app/components/Button/ButtonVariant";
import UserModel from "app/models/UserModel";
import UserAvatar from "app/components/UserAvatar";
import { cls } from "app/utils";
import SelectBox, { UserSelect } from "app/components/inputs/SelectBox";
import PhoneInput from "react-phone-input-2";
import { IUserPhone } from "app/interfaces/UserTypes";
import { useTranslation } from "react-i18next";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "typedefs";
import { hasAcademyPlan } from "app/utils/subscription";

type selectedUserType = {
  // CN-2834: the id of an existing member picked from the list. A managed family
  // member has no email and no phone, so the id is the only way the API can tell
  // who they are.
  userId?: string;
  name?: string;
  email?: string;
  phone?: null | string;
  phoneCountry?: null | string;
  role?: "coach" | "contributor";
  avatar?: null | string;
};

interface Props {
  onMemberInvite: (user: UserModel) => Promise<boolean>;
  invitationError?: string | null;
}

const SpaceMembersInvite = ({ onMemberInvite, invitationError }: Props) => {
  const { t, i18n } = useTranslation();
  const { self } = useSelector((state: RootState) => state.user, shallowEqual);
  const connections = useSelector(
    (state: RootState) => state.connection.data,
    shallowEqual,
  );
  const [inviteSectionOpen, setInviteSectionOpen] = useState(false);
  const [inviteWithPhone, setInviteWithPhone] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  // Guards against double clicks that land before the disabled state renders.
  const isInvitingRef = useRef(false);
  const [error, setError] = useState<string | null>(
    invitationError ? invitationError : null,
  );

  const initialSelectedUserValues: selectedUserType = {
    userId: null,
    name: null,
    email: null,
    phone: null,
    phoneCountry: null,
    role: "coach",
    avatar: null,
  };

  const [selectedUser, setSelectedUser] = useState<selectedUserType>(
    initialSelectedUserValues,
  );

  const resetStates = () => {
    setError(null);
    setInviteSectionOpen(false);
    setInviteWithPhone(false);
    setSelectedUser(initialSelectedUserValues);
  };

  const toggleInviteSection = () => {
    setSelectedUser(initialSelectedUserValues);
    setInviteSectionOpen(!inviteSectionOpen);
  };

  const selectUser = (user: UserModel | null) => {
    if (!user) {
      setSelectedUser(initialSelectedUserValues);
    } else {
      setSelectedUser({
        ...selectedUser,
        userId: connections?.some((connection) => connection.id === user.id)
          ? user.id
          : null,
        name: user.displayName,
        email: user.email,
        avatar: user.avatar as string,
      });
    }
  };

  const setUserName = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedUser) {
      // Typing over the name means a new invitee, not the one picked before.
      setSelectedUser({ ...selectedUser, userId: null, name: e.target.value });
    }
  };

  const setPhoneNumber = (value: string, data: IUserPhone) => {
    const phone = value.substring(data.dialCode.length);
    const phoneCountry = data.countryCode.toUpperCase();

    if (selectedUser) {
      setSelectedUser({ ...selectedUser, phone, phoneCountry });
    }
  };

  const roleOptions = useMemo(
    () => [
      { value: "coach", label: t("Coach") },
      { value: "contributor", label: t("Member") },
    ],
    [t, i18n.language],
  );

  const onRoleChange = (role: "coach" | "contributor") => {
    if (selectedUser) {
      setSelectedUser({ ...selectedUser, role });
    }
  };

  const onInvite = () => {
    if (isInvitingRef.current) {
      return;
    }
    // An id identifies the member on its own. Without one the API needs an email
    // or a phone number, and a name alone is not enough for it to invite anybody.
    if (!selectedUser.userId) {
      if (!inviteWithPhone) {
        if (!selectedUser.name || !selectedUser.email) {
          setError(t("Can't be blank"));
          return;
        }
      } else if (!selectedUser.phone && !selectedUser.phoneCountry) {
        setError(t("Can't be blank"));
        return;
      }
    }
    setError(null);
    isInvitingRef.current = true;
    setIsInviting(true);
    onMemberInvite({
      userId: selectedUser.userId,
      email: selectedUser.email,
      name: selectedUser.name,
      phone: selectedUser.phone,
      phoneCountry: selectedUser.phoneCountry,
      role: selectedUser.role,
    })
      .then((success) => {
        if (success) {
          resetStates();
        }
      })
      .finally(() => {
        isInvitingRef.current = false;
        setIsInviting(false);
      });
  };

  const onCancel = () => {
    setInviteSectionOpen(false);
    resetStates();
  };

  return (
    <div className={styles.container}>
      {!inviteSectionOpen ? (
        <Button
          buttonType="primary"
          icon={<i className="icon-plus icon-white" />}
          title={t("Invite New Members")}
          buttonStyles={styles.inviteButton}
          onClickFunc={toggleInviteSection}
        />
      ) : (
        <div className={styles.invitation_form_container}>
          <label>{t("Invite New Member")}</label>
          {!inviteWithPhone ? (
            <>
              {selectedUser && selectedUser.name ? (
                <div className={styles.user_info_container}>
                  <div className={styles.user_info_left}>
                    <UserAvatar
                      className={styles.avatar}
                      sizeWidth={44}
                      sizeHeight={44}
                      altName="author"
                      userName={selectedUser.name}
                      srcName={selectedUser.avatar as string}
                    />
                    <div className={styles.user_info}>
                      <h4>{selectedUser.name}</h4>
                      <p>{selectedUser.email}</p>
                    </div>
                  </div>
                  <i
                    className={cls("icon-remove-sign", styles.remove_sign)}
                    onClick={() => selectUser(null)}
                  />
                </div>
              ) : (
                <UserSelect
                  onChange={(user: any) =>
                    user ? selectUser(user[0].model) : null
                  }
                  isCreatable
                />
              )}
            </>
          ) : (
            <div className={styles.invite_by_wrapper}>
              <input
                onChange={setUserName}
                value={selectedUser && selectedUser.name}
                placeholder={t("Name")}
              />
              <PhoneInput
                country={
                  selectedUser.phoneCountry &&
                  selectedUser.phoneCountry.length > 0
                    ? selectedUser.phoneCountry
                    : "us"
                }
                autoFormat
                onChange={setPhoneNumber}
                containerClass={styles.phone_input_container}
                inputClass={styles.form_control}
              />
            </div>
          )}
          {(invitationError || error) && (
            <p className={styles.error}>{t(invitationError) || t(error)}</p>
          )}
          <div className={styles.section_wrapper}>
            <label htmlFor="role">{t("Role")}</label>
            <SelectBox
              // Remount on a language change so the selected label is re-translated.
              key={i18n.language}
              className={styles.role_select}
              inputId="role"
              isCreatable={false}
              isClearable={false}
              isSearchable={false}
              options={roleOptions}
              defaultValue={
                roleOptions.find((o) => o.value === selectedUser.role) ||
                roleOptions[0]
              }
              onChange={(option: { value: "coach" | "contributor" }) =>
                onRoleChange(option.value)
              }
            />
          </div>
          {!hasAcademyPlan(self) && (
            <div
              className={styles.add_by_btn}
              onClick={() => setInviteWithPhone(!inviteWithPhone)}
            >
              {inviteWithPhone
                ? t("Add by Name or Email")
                : t("Add by Phone Number")}
            </div>
          )}
          <div className={styles.button_wrapper}>
            <Button
              buttonType="primary"
              title={t("Invite member")}
              onClickFunc={onInvite}
              disabled={isInviting}
              loading={isInviting}
            />
            <Button
              buttonType="secondary-outline"
              title={t("Cancel")}
              onClickFunc={onCancel}
              disabled={isInviting}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SpaceMembersInvite;
